/* eslint-env node */

const { property, isEmpty } = require("lodash");
const {
  abbrState,
  cleanString,
  toNumber,
  toPlainText,
  truncate,
  toIso,
  escapeHtmlAttr,
  escapeJsonForScript,
} = require("../helpers");

const buildPdpUrl = (details, origin) => {
  const slug = [
    cleanString(details.loc_address),
    cleanString(details.loc_city),
    abbrState(details.state) || details.state,
    details.zipcode,
  ]
    .filter(Boolean)
    .join("-");
  return `${origin}/property/${details.id}${slug ? `/${slug}` : ""}`;
};

const getSaleTypes = (details) => {
  const types = [];
  if (Number(details.auction) === 1) types.push("Online Auction");
  if (Number(details.buynow) === 1) types.push("Buy Now");
  if (Number(details.offer) === 1) types.push("Make an Offer");
  return types;
};

const getTimeRemaining = (details, serverTime) => {
  if (!details.date_closed) return null;
  const end = new Date(String(details.date_closed).replace(" ", "T")).getTime();
  const now = serverTime
    ? new Date(String(serverTime).replace(" ", "T")).getTime()
    : Date.now();
  const diff = end - now;
  if (!Number.isFinite(diff) || diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
  }
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    ended: false,
  };
};

/**
 * @param {object} details Flattened property details.
 * @param {string[]} imageUrls Absolute image URLs.
 * @param {object} opts { serverTime, origin, siteName }
 * @returns {object|null} { metaJson, jsonLd, title, description, ogImage, canonical, headTags }
 */
const buildPropertySeo = (details = {}, imageUrls = [], opts = {}) => {
  if (!details || !details.id) return null;

  const siteName = opts.siteName || "Next Door Auctions";
  const origin = opts.origin || "https://nextdoorauctions.com/";
  const canonical = buildPdpUrl(details, origin);
  const images = (imageUrls || []).filter(
    (url) => typeof url === "string" && url && !url.startsWith("/images/"),
  );

  const saleTypes = getSaleTypes(details);
  const timeRemaining = getTimeRemaining(details, opts.serverTime);

  const openingBid = toNumber(details.sprice);
  const currentBid = toNumber(details.wprice);
  const buyNowPrice = toNumber(details.bprice);
  const listPrice = toNumber(details.mls_price);
  const nextBid = toNumber(details.next_bid);
  const primaryPrice = currentBid || openingBid || buyNowPrice || listPrice;

  const descriptionText = toPlainText(details.description);

  const addressParts = [
    details.loc_address,
    details.loc_city,
    [details.state, details.zipcode].filter(Boolean).join(" "),
  ].filter(Boolean);
  const addressLine = addressParts.join(", ");

  const title = addressLine
    ? `${addressLine} | ${siteName}`
    : `Property #${details.id} | ${siteName}`;
  const metaDescription =
    truncate(descriptionText) ||
    `View auction details, photos and bidding information for ${
      addressLine || "this property"
    } on ${siteName}.`;

  const isFlag = (value) => Number(value) === 1;
  const orNull = (value) =>
    value === null || value === undefined || value === "" ? null : value;

  const titleProperty = addressLine
    ? `${addressLine}`
    : `Property #${details.id}`;

  const propertyData = {
    listing_id: details.id,
    url: canonical,
    title: titleProperty,
    description: descriptionText || null,
    marketing_description:
      details.marketing_description == null
        ? ""
        : toPlainText(details.marketing_description),
    about_property: isEmpty(details.aboutProperty)
      ? ""
      : toPlainText(details.aboutProperty),
    owner_description: isEmpty(details.ownerDescription)
      ? ""
      : toPlainText(details.ownerDescription),
    featured_description: isEmpty(details.featured_des)
      ? ""
      : toPlainText(details.featured_des) || null,
    market_status: details.market_status || null,
    listing_tier: details.listing_tier != null ? details.listing_tier : null,
    product_type: details.product_type || null,
    cash_only: isFlag(details.cash_only_flag),
    keywords: orNull(details.keywords),
    file_case: details?.contact_file_case,
    attorney_trustee: details?.contact_attorney_trustee,
    images,
    address: {
      street: details.loc_address || null,
      address_line: orNull(details.loc_address_line),
      house_number: details.house_number || null,
      city: details.loc_city || null,
      locality: orNull(details.loc_locality),
      county: details.county || null,
      state: details.state || null,
      zip: details.zipcode || null,
      landmark: orNull(details.landmark),
      society_name: orNull(details.society_name),
      //build_name: orNull(details.build_name),
      sell_location: orNull(details.sell_location),
      latitude: toNumber(details.lat),
      longitude: toNumber(details.lng),
    },
    auction: {
      types: saleTypes,
      type: saleTypes[0] || null,
      format: orNull(details.auction_format),
      inperson_location: orNull(details.auction_inperson_location),
      duration: orNull(details.auction_duration),
      opening_bid: openingBid,
      current_bid: currentBid,
      buynow_price: buyNowPrice,
      list_price: listPrice,
      increment_price: toNumber(details.iprice),
      bid_increment: toNumber(details.bidincrement),
      next_bid: nextBid,
      bid_count: toNumber(details.bidcount) || 0,
      unique_bidders: toNumber(details.uniqueBidders) || 0,
      views: toNumber(details.views) || 0,
      qty: toNumber(details.qty),
      //sold: isFlag(details.sold),
      seller_terms_enabled: isFlag(details.enable_seller_terms),
      seller_terms: toPlainText(details.sellerTerms) || null,
      starts: toIso(details.date_added),
      ends: toIso(details.date_closed),
      start_date: toIso(details.start_date),
      end_date: toIso(details.end_date),
      buynow_start_date: toIso(details.buynow_start_date),
      buynow_end_date: toIso(details.buynow_end_date),
      offer_start_date: toIso(details.offer_start_date),
      offer_end_date: toIso(details.offer_end_date),
      time_remaining: timeRemaining,
    },
    property_info: {
      listing_status: details?.listing_status,
      property_type: orNull(details.property_type),
      home_style: orNull(details.homestyle),
      beds: toNumber(details.bedroom_type),
      baths: toNumber(details.bathroom),
      square_footage: toNumber(details.sqrtarea),
      super_built_area: toNumber(details.super_built_area),
      built_area: toNumber(details.built_area),
      carpet_area: toNumber(details.carpet_area),
      lot_size: toNumber(details.stories),
      lot_size_unit: details.storiesUnit || null,
      year_built: details.Ybuild || null,
      new_construction: isFlag(details.new_construction),
      garage: orNull(details.garage),
      parking: orNull(details.parking),
      heating: orNull(details.heating),
      roof: orNull(details.roof),
      hoa: orNull(details.hoa),
      school: orNull(details.school),
      ownership: orNull(details.ownership),
      //possession: orNull(details.possesion),
      facing: orNull(details.facing),
      floors: toNumber(details.no_of_floor),
      floor: orNull(details.floor),
      occupancy_status: orNull(
        details.occupancy_status || details.occupency_type,
      ),
      apn: details.parcel_id_apn || null,
    },
    financials: {
      property_tax: toNumber(details.ptax),
      home_insurance: toNumber(details.houseInsurance),
      hoa: orNull(details.hoa),
      buyers_premium: toNumber(details.buyers_premium),
      buyers_premium_minimum: toNumber(details.buyers_premium_minimum),
      buyers_premium_flag: isFlag(details.buyers_premium_flag),
      real_estate_commission: toNumber(details.real_estate_commission),
      seller_loan: toNumber(details.seller_loan),
      financing_type: orNull(details.financingType),
      cash_only: isFlag(details.cash_only_flag),
    },
    agent: {
      agent_id: orNull(details.agent_id),
      listing_agent_name: orNull(details.listing_agent_name),
      listing_agent_email: orNull(details.listing_agent_email),
      listing_agent_phone: orNull(details.listing_agent_phone),
      license: orNull(details.license),
    },
    auctioneer: {
      auctioneer_name: orNull(details.auctioneer_name),
      auctioneer_license: orNull(details.auctioneer_license),
      auction_firm_name: orNull(details.auction_firm_name),
      auction_firm_license: orNull(details.auction_firm_license),
    },
    brokerage: {
      brokerage_name: orNull(details.brokerage_name),
    },
    contact: {
      name: orNull(details.contact_name),
      email: orNull(details.contact_email),
      phone: orNull(details.contact_phone),
    },
    legal: {
      property_disclosures: toPlainText(details.property_disclosures) || null,
      auction_disclosures: toPlainText(details.auction_disclosures) || null,
    },
    media: {
      document: orNull(details.document),
      owner_pdf: orNull(details.ownerPdf),
      video_url: orNull(details.video_url || details.listing_video),
      matterport: orNull(details.matterport),
      matterport_enabled: isFlag(details.show_matterport),
      listing_link: orNull(details.listingLink),
    },
    inspection: {
      enabled: isFlag(details.enable_inspection),
      inspected_by: orNull(details.inspectedBy),
      comps_enabled: isFlag(details.enable_comps),
    },
  };

  const additionalProperty = [
    ["Bedrooms", toNumber(details.bedroom_type)],
    ["Bathrooms", toNumber(details.bathroom)],
    ["Square Footage", toNumber(details.sqrtarea)],
    ["Year Built", details.Ybuild],
    ["County", details.county],
    [
      "Lot Size",
      details.stories
        ? `${details.stories}${details.storiesUnit ? ` ${details.storiesUnit}` : ""}`
        : null,
    ],
  ]
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .map(([name, value]) => ({
      "@type": "PropertyValue",
      name,
      value: `${value}`,
    }));

  const availability =
    timeRemaining && timeRemaining.ended
      ? "https://schema.org/SoldOut"
      : "https://schema.org/InStock";

  const place = {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      streetAddress: details.loc_address || undefined,
      addressLocality: details.loc_city || undefined,
      addressRegion: details.state || undefined,
      postalCode: details.zipcode || undefined,
      addressCountry: "US",
    },
  };
  const lat = toNumber(details.lat);
  const lng = toNumber(details.lng);
  if (lat !== null && lng !== null) {
    place.geo = { "@type": "GeoCoordinates", latitude: lat, longitude: lng };
  }

  const offer = {
    "@type": "Offer",
    url: canonical,
    priceCurrency: "USD",
    availability,
    availableAtOrFrom: place,
  };
  if (primaryPrice !== null) offer.price = primaryPrice;
  const priceValidUntil = toIso(details.date_closed);
  if (priceValidUntil) offer.priceValidUntil = priceValidUntil;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: addressLine || `Property #${details.id}`,
    category: "Real Estate",
    sku: `${details.id}`,
    url: canonical,
    offers: offer,
  };
  if (descriptionText) jsonLd.description = truncate(descriptionText, 5000);
  if (images.length) jsonLd.image = images;
  if (additionalProperty.length) jsonLd.additionalProperty = additionalProperty;

  const removeEmptyFields = (obj) => {
    if (Array.isArray(obj)) {
      const arr = obj
        .map(removeEmptyFields)
        .filter(
          (v) =>
            v !== null &&
            v !== undefined &&
            v !== "" &&
            v !== "null" &&
            v !== "[object Object]" &&
            !(Array.isArray(v) && v.length === 0),
        );

      return arr.length ? arr : undefined;
    }

    if (obj && typeof obj === "object") {
      const cleaned = Object.entries(obj).reduce((acc, [key, value]) => {
        const val = removeEmptyFields(value);

        const isEmptyObject =
          val &&
          typeof val === "object" &&
          !Array.isArray(val) &&
          Object.keys(val).length === 0;

        if (
          val !== null &&
          val !== undefined &&
          val !== "" &&
          val !== "null" &&
          val !== "[object Object]" &&
          !isEmptyObject
        ) {
          acc[key] = val;
        }

        return acc;
      }, {});

      return Object.keys(cleaned).length ? cleaned : undefined;
    }

    return obj;
  };

  const cleanedPropertyData = removeEmptyFields(propertyData);
  const metaJson = JSON.stringify(cleanedPropertyData);

  // Ready-to-inject <head> fragment for the server. Tags carry data-react-helmet
  // so the client SPA reconciles (replaces) them instead of duplicating.
  const rh = 'data-react-helmet="true"';
  const headTags = [
    `<meta ${rh} name="property-data" content="${escapeHtmlAttr(metaJson)}" />`,
    `<meta ${rh} name="description" content="${escapeHtmlAttr(metaDescription)}" />`,
    `<link ${rh} rel="canonical" href="${escapeHtmlAttr(canonical)}" />`,
    `<meta ${rh} property="og:title" content="${escapeHtmlAttr(title)}" />`,
    `<meta ${rh} property="og:description" content="${escapeHtmlAttr(metaDescription)}" />`,
    `<meta ${rh} property="og:type" content="product" />`,
    `<meta ${rh} property="og:url" content="${escapeHtmlAttr(canonical)}" />`,
    images[0]
      ? `<meta ${rh} property="og:image" content="${escapeHtmlAttr(images[0])}" />`
      : "",
    `<meta ${rh} name="twitter:card" content="summary_large_image" />`,
    `<meta ${rh} name="twitter:title" content="${escapeHtmlAttr(title)}" />`,
    `<meta ${rh} name="twitter:description" content="${escapeHtmlAttr(metaDescription)}" />`,
    images[0]
      ? `<meta ${rh} name="twitter:image" content="${escapeHtmlAttr(images[0])}" />`
      : "",
    `<script ${rh} type="application/ld+json">${escapeJsonForScript(
      JSON.stringify(jsonLd),
    )}</script>`,
  ]
    .filter(Boolean)
    .join("\n    ");

  return {
    metaJson,
    jsonLd,
    title,
    description: metaDescription,
    ogImage: images[0] || null,
    canonical,
    headTags,
  };
};

module.exports = { buildPropertySeo };
